<template>
  <img src="/Logo-Black.png" alt="Playtronica logo" width="140" loading="eager" class="small--hide image-element" itemprop="logo">

  <header class="d-flex justify-content-center">

      <ul class="nav nav-pills">
      <li class="nav-item">
        <router-link  to="/biotron" @click="this.update" class="nav-link">Biotron</router-link>
      </li>
      <li class="nav-item">
        <router-link  to="/touchme" @click="this.update" class="nav-link">TouchMe</router-link>
      </li>
      <li class="nav-item">
        <router-link to="/playtron" @click="this.update" class="nav-link">Playtron</router-link>
      </li>
        <li class="nav-item">
          <router-link to="/scales" @click="this.update" class="nav-link">Scales</router-link>
        </li>
      <li class="nav-item">
        <a href="https://playtronica.github.io/WebMidiOrbita/?nomidi=true" class="nav-link" aria-current="page">Orbita</a>
      </li>
    </ul>
  </header>
  <div v-if="betaBuild" class="beta-meta mx-auto mt-2">
    <small class="beta-build" :title="`Build ${buildId}`">Beta</small>
    <div
        v-if="offlineMessage"
        class="offline-status px-2 py-1"
        :class="offlineStatusClass"
        role="status"
        aria-live="polite"
    >
      <span>{{ offlineMessage }}</span>
      <button
          v-if="offlineStatus.ready && online && !installed && installPrompt"
          type="button"
          class="offline-action"
          @click="installApp"
      >
        Install
      </button>
      <button
          v-if="offlineStatus.state === 'error'"
          type="button"
          class="offline-action offline-action--error"
          @click="retryOfflineSetup"
          :disabled="offlineRetrying"
      >
        {{ offlineRetrying ? "Retrying…" : "Retry" }}
      </button>
    </div>
  </div>
  <div class="wrapper">
    <div class="m-2 content ">
      <router-view></router-view>

    </div>
    <footer class="bottom-panel">
      <SocialLinks/>
    </footer>
  </div>
</template>


<script>
import SocialLinks from "@/components/SocialLinks.vue";
import {
  getOfflineStatus,
  OFFLINE_STATUS_EVENT,
  prepareOfflineAccess
} from "@pwa-entry";

const runningStandalone = () => window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true


export default {
  name: 'App',
  components: {SocialLinks},
  data() {
    return {
      url: String,
      forceRerender: 0,
      offlineStatus: getOfflineStatus(),
      online: navigator.onLine,
      installPrompt: null,
      installed: runningStandalone(),
      offlineRetrying: false,
      betaBuild: process.env.VUE_APP_BIOTRON_PWA_BETA === 'true',
      buildId: process.env.VUE_APP_BUILD_ID || 'local-build'
    }
  },
  computed: {
    offlineMessage() {
      if (this.offlineStatus.ready && !this.online) {
        return "Working offline · Firmware needs internet"
      }
      if (this.offlineStatus.ready) {
        return "Offline ready"
      }
      if (this.offlineStatus.state === "installing") {
        return "Preparing offline access…"
      }
      if (this.offlineStatus.state === "error") {
        const messages = {
          SW_FIRST_INSTALL_OFFLINE: "Connect once to install the offline copy, then press Retry.",
          SW_NO_CONTROLLER: "Close every Settings window, reopen this page, then press Retry."
        }
        return messages[this.offlineStatus.code] || "Offline setup did not finish. Check the connection, then press Retry."
      }
      if (this.offlineStatus.state === "unsupported") {
        return "This browser cannot install Settings for offline use."
      }
      return ""
    },
    offlineStatusClass() {
      if (this.offlineStatus.ready) return "offline-status--ready"
      if (this.offlineStatus.state === "error" || this.offlineStatus.state === "unsupported") {
        return "offline-status--error"
      }
      return "offline-status--preparing"
    }
  },
  mounted() {
    console.log("Hello! You`re curious, aren`t you?")
    window.addEventListener(OFFLINE_STATUS_EVENT, this.handleOfflineStatus)
    window.addEventListener("online", this.handleConnectionChange)
    window.addEventListener("offline", this.handleConnectionChange)
    window.addEventListener("beforeinstallprompt", this.handleInstallPrompt)
    window.addEventListener("appinstalled", this.handleInstalled)
  },
  beforeUnmount() {
    window.removeEventListener(OFFLINE_STATUS_EVENT, this.handleOfflineStatus)
    window.removeEventListener("online", this.handleConnectionChange)
    window.removeEventListener("offline", this.handleConnectionChange)
    window.removeEventListener("beforeinstallprompt", this.handleInstallPrompt)
    window.removeEventListener("appinstalled", this.handleInstalled)
  },
  methods: {
    update() { this.forceRerender++ },
    handleOfflineStatus(event) {
      this.offlineStatus = event.detail
    },
    handleConnectionChange() {
      this.online = navigator.onLine
    },
    handleInstallPrompt(event) {
      event.preventDefault()
      this.installPrompt = event
    },
    handleInstalled() {
      this.installPrompt = null
      this.installed = true
    },
    async installApp() {
      const prompt = this.installPrompt
      if (!prompt) return
      this.installPrompt = null
      await prompt.prompt()
      await prompt.userChoice
    },
    async retryOfflineSetup() {
      this.offlineRetrying = true
      try {
        this.offlineStatus = await prepareOfflineAccess()
      } finally {
        this.offlineRetrying = false
      }
    }
  }
}
</script>

<style>

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 1%;
}

.beta-meta {
  width: min(720px, calc(100% - 2rem));
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .6rem;
}

.beta-build {
  color: #6c757d;
  white-space: nowrap;
}

.offline-status {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  border: 1px solid;
  border-radius: 999px;
  font-size: .8rem;
}

.offline-action {
  min-height: 28px;
  padding: 0.15rem 0.55rem;
  border: 0;
  border-radius: 999px;
  color: #0f5132;
  background: #fff;
  font: inherit;
  font-weight: 600;
}

.offline-action--error {
  margin-left: 0.75rem;
  color: #842029;
}

@media (max-width: 640px) {
  .beta-meta {
    align-items: center;
    flex-wrap: wrap;
    gap: .35rem .6rem;
  }

  header .nav {
    max-width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
  }

  header .nav::-webkit-scrollbar { display: none; }
  header .nav-link {
    padding: .45rem .55rem;
    white-space: nowrap;
  }
}

.offline-status--ready {
  color: #0f5132;
  background: #d1e7dd;
  border-color: #badbcc;
}

.offline-status--preparing {
  color: #664d03;
  background: #fff3cd;
  border-color: #ffecb5;
}

.offline-status--error {
  color: #842029;
  background: #f8d7da;
  border-color: #f5c2c7;
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked + .slider {
  background-color: #2196F3;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196F3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.content {
  flex: 1;
  padding: 20px;
  box-sizing: border-box;
}

.wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}


.bottom-panel {
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px black dashed;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Hide spin buttons in Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
